import React from 'react';
import clsx from 'clsx';
import styles from './PaginationControls.module.css';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type PageChangeHandler = (page: number) => void;
type PageSizeChangeHandler = (pageSize: number) => void;

export interface PaginationControlsProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  currentCount: number;
  onPageChange: PageChangeHandler;
  onPageSizeChange: PageSizeChangeHandler;
  showSummaryText?: boolean;
  pageSizeOptions?: number[];
  rowsLabel?: string;
  className?: string;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  pageSize,
  totalCount,
  currentCount,
  onPageChange,
  onPageSizeChange,
  showSummaryText = true,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowsLabel = 'Rows:',
  className
}) => {
  const startItem = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = totalCount > 0 ? Math.min(startItem + currentCount - 1, totalCount) : 0;
  const hasPrev = currentPage > 0;
  const hasNext = (currentPage + 1) * pageSize < totalCount;
  const selectId = React.useId();

  return (
    <div className={clsx(styles.container, className)}>
      {showSummaryText && (
        <div className={styles.summary}>
          {totalCount > 0 ? `Showing ${startItem}-${endItem} of ${totalCount}` : 'No results'}
        </div>
      )}
      <div className={styles.actions}>
        <div className={styles.selectGroup}>
          <label className={styles.selectLabel} htmlFor={selectId}>{rowsLabel}</label>
          <select
            id={selectId}
            name="pageSizeSelector"
            value={pageSize}
            onChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
            className={styles.select}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => onPageChange(Math.max(currentPage - 1, 0))}
          disabled={!hasPrev}
          className={styles.button}
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext}
          className={styles.button}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PaginationControls;
